import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, HostBinding, OnDestroy, OnInit, Output, inject, input, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { StreamTypes } from "@auvious/rtc";
import { merge } from "rxjs";

import { FormGroup } from "@angular/forms";
import { MediaDevices } from "@auvious/media-tools";
import { localStore } from "@auvious/utils";
import { KEY_USER_DISPLAY_NAME } from "../../../app/app.enums";
import { fadeInOut, slideIn, slideInOut } from "../../core-ui.animations";
import {
  ConversationTypeEnum,
  NotificationSoundEnum,
  StreamTrackKindEnum,
  UserRoleEnum,
} from "../../core-ui.enums";
import { CustomerParam, IDeviceSetupOptions, ITile } from "../../models";
import { IUserDetails } from "../../models/IUser";
import {
  ActivityIndicatorService,
  ConferenceStore,
  debugError,
  DeviceService,
  KeyboardService,
  KeyCodes,
  LocalMediaService,
  MediaEffectsService,
  MediaRulesService,
  StreamState,
  UserService,
  VoiceDetectionService,
} from "../../services";
import { AppConfigService } from "../../services/app.config.service";
import { NotificationService } from "../../services/notification.service";
import { RoomComponent } from "../room/room.component";
import { TileComponent } from "../tile/tile.component";
import { WindowService } from "../../services/window.service";
import { markFormFields } from "../../../app/app.utils";
import { BackgroundEffectsComponent } from "../background-effects/background-effects.component";

@Component({
  selector: "app-device-setup",
  templateUrl: "./device-setup.component.html",
  styleUrls: ["./device-setup.component.scss"],
  animations: [slideIn, slideInOut, fadeInOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DeviceSetupComponent implements OnInit, OnDestroy, AfterViewInit {
  private deviceService = inject(DeviceService);
  private mediaService = inject(LocalMediaService);
  private voiceDetectionService = inject(VoiceDetectionService);
  private notification = inject(NotificationService);
  private userService = inject(UserService);
  private cd = inject(ChangeDetectorRef);
  private mediaFilter = inject(MediaEffectsService);
  private activity = inject(ActivityIndicatorService);
  private mediaRules = inject(MediaRulesService);
  private store = inject(ConferenceStore);
  private winService = inject(WindowService);
  private keyboard = inject(KeyboardService);
  private room = inject(RoomComponent, { optional: true });
  private destroyRef = inject(DestroyRef);

  readonly volumeElem = viewChild<ElementRef<HTMLDivElement>>("volumeRef");
  readonly tileRef = viewChild<TileComponent>("tile");

  readonly conversationType = input<ConversationTypeEnum>(undefined);

  @Output() canceled = new EventEmitter<void>();
  @Output() accepted = new EventEmitter<IDeviceSetupOptions>();

  readonly effectsMenuRef = viewChild("effectsMenu", { read: ElementRef });

  readonly effectsMenuButtonRef = viewChild("effectsMenuButton", { read: ElementRef });

  readonly effectsRef = viewChild<BackgroundEffectsComponent>("effectsRef");

  isJoining: boolean;
  isAgent: boolean;
  isCustomer: boolean;
  devicesOpen = false;
  isTileReady = false;
  skipSetup = false;
  isGuest: boolean;
  isApplyingFilter = false;
  isSwitching = false;
  isEffectsOpen = false;
  isTogglingCam = false;
  isRequestingName = true;
  participantName: string;

  micOn = true;
  camOn = true;
  stream: StreamState;

  constructor() {
    const config = inject(AppConfigService);
    this.isAgent = this.userService.getActiveUser().hasRole(UserRoleEnum.agent);
    this.isCustomer = this.userService
      .getActiveUser()
      .hasRole(UserRoleEnum.customer);
    this.isJoining = false;
    this.participantName = this.getStoredDisplayName();
    this.isRequestingName =
      this.isCustomer &&
      config.customerParamEnabled(CustomerParam.DISPLAY_NAME_REQUIRED);
  }

  ngOnInit() {
    // audio stream is opened at openStreamEnumerateDevices, so we need to sync ui here.
    // would be better to have a central redux store to ask for stuff
    if (this.conversationType() === ConversationTypeEnum.voiceCall) {
      this.camOn = false;
    }

    this.mediaService.speakerChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.testSpeaker();
    });

    this.store.updated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.stream !== this.store.mystream) {
        this.stream = this.store.mystream;
        this.camOn = this.stream?.video.state === "enabled";
        this.micOn = this.stream?.audio.state === "enabled";
        this.isEffectsOpen &&=
          this.stream &&
          (this.stream.type === StreamTypes.CAM ||
            this.stream.type === StreamTypes.VIDEO);

        this.cd.detectChanges();
      }
    });

    this.mediaService.addedDevices$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((devices) => {
      this.cd.detectChanges();
    });

    merge(
      this.mediaFilter.filterDisabled$,
      this.mediaFilter.filterError$
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isApplyingFilter = false;
      this.cd.detectChanges();
    });
  }

  ngAfterViewInit() {
    // no need to filter the streams, at this point we are alone
    this.voiceDetectionService.volumeLevel$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((volume) => {
      const volumeElem = this.volumeElem();
      if (volumeElem) {
        volumeElem.nativeElement.style.height = volume.level + "px";
      }
    });

    (
      document.querySelector(
        "[data-tid='device-setup/accepts-constraints']"
      ) as HTMLButtonElement
    )?.focus();

    this.keyboard.listen(KeyCodes.Esc, this.onEscKey.bind(this));
    if (this.isVideoDeviceAvailable) {
      this.keyboard.listen(KeyCodes.V, this.onVKey.bind(this));
    }
  }

  onEffectsFocusOut(event) {
    if (
      !this.effectsMenuRef().nativeElement.contains(event.relatedTarget) &&
      event.relatedTarget?.id !== "effects-close" && // not clicking on close button
      this.isEffectsOpen
    ) {
      // Focus moved outside the modal
      this.toggleEffects();
    }
  }

  effectsNavigateOut() {
    this.effectsMenuButtonRef().nativeElement.focus();
  }

  private onEscKey() {
    if (this.isEffectsOpen) {
      this.toggleEffects();
    } else {
      this.cancel();
    }
  }

  private onVKey() {
    if (this.isTogglingCam || this.isVideoDeviceMissing) {
      return;
    }
    this.toggleCam(!this.camOn);
  }

  @HostBinding("class") get class() {
    return {
      "device-setup-mobile": this.isMobile,
    };
  }

  async tileReady(tile: ITile) {
    // give a moment for dom to change
    await new Promise(requestAnimationFrame);
    const tileRef = this.tileRef();
    if (tileRef) {
      const width = tileRef.container.clientHeight * tile.ratio;
      tileRef.container.style.width = width + "px";
      this.isTileReady = true;
    }
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    this.keyboard.unlisten(KeyCodes.Esc, this.onEscKey.bind(this));
  }

  /* actions */

  async toggleCam(on: boolean) {
    try {
      this.isTileReady = false;
      this.isTogglingCam = true;
      if (!on) {
        await this.mediaService.mute(StreamTrackKindEnum.video);
      } else {
        await this.mediaService.unmute(StreamTrackKindEnum.video);
      }
    } catch (ex) {
      this.isTileReady = true;
      setTimeout(() => {
        this.camOn = false;
        this.cd.detectChanges();
      }, 100);
      this.notification.error("Could not connect to camera");
    } finally {
      this.isTogglingCam = false;
      this.cd.detectChanges();
    }
  }

  toggleEffects() {
    this.isEffectsOpen = !this.isEffectsOpen;
    this.cd.detectChanges();

    if (this.isEffectsOpen) {
      this.winService
        .onClickedOutside(
          this.effectsMenuRef().nativeElement,
          this.effectsMenuButtonRef().nativeElement
        )
        .then(() => {
          if (this.isEffectsOpen) this.toggleEffects();
        });
    }
  }

  async switchCam() {
    if (this.isSwitching) {
      return;
    }

    this.isSwitching = true;
    this.activity.loading(true, "switching");

    try {
      await this.mediaService.switchCamera();
    } catch (err) {
      this.notification.error("Switch camera failed", {
        body: "Could not use this camera device. Reverted back to the previous one.",
      });

      debugError(err);
    } finally {
      this.isSwitching = false;
      this.activity.hide();
      this.cd.detectChanges();
    }
  }

  toggleDevicesPopup() {
    this.devicesOpen = !this.devicesOpen;
  }

  async testSpeaker() {
    this.notification.playSound(NotificationSoundEnum.speakerTest);
  }

  effectsKeyDown(event) {
    if (this.isEffectsOpen && event.key === "ArrowRight") {
      this.effectsRef().navigateIn();
    }
  }

  cancel() {
    this.canceled.emit();
  }

  acceptConstraints(form: FormGroup, event: Event) {
    event.preventDefault();

    if (this.isJoining) {
      return;
    }

    if (!form.valid) {
      markFormFields(form);
      return;
    }

    // update participant name
    if (this.isRequestingName) {
      const details: IUserDetails = this.userService.getUserDetails() || {
        id: this.userService.getActiveUser().getId(),
        name: null,
        displayName: null,
      };
      details.displayName = this.participantName;
      this.userService.setUserDetails(details);
      localStore.setItem(KEY_USER_DISPLAY_NAME, this.participantName);
    }

    this.isJoining = true;
    this.isEffectsOpen = false;
    this.deviceService.isDevicesSetup = true;

    // skip setup for widget as well
    if (this.skipSetup || this.isOriginWidget) {
      // store we have accepted the device constraints, not to show again the device setup
      this.deviceService.keepDeviceSetupUntil(
        this.mediaService.audioDevice?.deviceId,
        this.mediaService.videoDevice?.deviceId,
        new Date().toISOString()
      );
    }

    this.accepted.emit();
  }

  setDefaults() {
    MediaDevices.savePreferred({
      audioinput: this.mediaService.audioDevice?.deviceId,
      videoinput: this.mediaService.videoDevice?.deviceId,
      audiooutput: this.mediaService.speakerDevice?.deviceId,
    });

    this.notification.success("Saved", { body: "Devices set as defaults" });
  }

  /* getters */

  getStoredDisplayName(): string {
    return (
      this.userService.getUserDetails()?.displayName ||
      this.userService.getUserDetails()?.name
    );
  }

  get audioDeviceId() {
    return this.mediaService.audioDevice?.deviceId;
  }

  get videoDeviceId() {
    return this.mediaService.videoDevice?.deviceId;
  }

  get isJoinDisabled() {
    return this.isJoining; // || !this.stream;
  }

  get isMobile() {
    return DeviceService.isMobile;
  }

  get isPhone() {
    return DeviceService.isPhone;
  }

  get isOriginWidget() {
    return this.room?.isWidget;
  }

  get isFilterAvailable() {
    return this.isVideoDeviceAvailable && this.mediaFilter.supportsMediaFilters;
  }

  get isFilterOn() {
    return this.mediaFilter.isMediaFilterOn || this.isApplyingFilter;
  }

  get isMediaAvailable() {
    return MediaDevices.has.videoinput || this.isAudioAvailable;
  }

  get isVideoDeviceAvailable() {
    return (
      MediaDevices.has.videoinput &&
      this.conversationType() !== ConversationTypeEnum.voiceCall &&
      // if we don't have audio we should at least have video to join the call
      this.mediaRules.isVideoControlAvailable
    );
  }

  get isVideoDeviceMissing() {
    return MediaDevices.has.videoinput === 0;
  }

  get isAudioAvailable() {
    return MediaDevices.has.audioinput && this.mediaRules.isAudioAvailable;
  }

  get isAudioDeviceMissing() {
    return MediaDevices.has.audioinput === 0;
  }

  get isAudioVisible() {
    return (
      this.isAudioAvailable &&
      // does not make sense to have mic shown
      this.mediaRules.isVideoControlAvailable
    );
  }

  get isAudioEnabled() {
    return !this.isAudioDeviceMissing && this.micOn;
  }
}
